(() => {
  const config = window.kuddyTrackingConfig || {};
  const GA_ID = String(config.google_analytics_id || '').trim();
  const FB_ID = String(config.meta_pixel_id || '').trim();
  const DEFAULT_CURRENCY = 'NGN';
  const CONSENT_COOKIE = 'kuddy_tracking_consent';

  const state = {
    consent: false,
    inited: false,
    queue: [],
    googleReady: false,
    metaReady: false,
  };

  window.dataLayer = window.dataLayer || [];

  const toString = (value) => String(value || '').trim();
  const toNumber = (value, fallback = 0) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  };
  const toMoney = (value, opts = {}) => {
    const unit = toString(opts.unit || 'minor').toLowerCase();
    const number = toNumber(value, 0);
    if (!number) return 0;

    if (unit === 'major') return Number(number);
    if (unit === 'minor') return Number(number) / 100;

    if (!Number.isInteger(number) || number > 100000) return Number(number) / 100;
    return Number(number);
  };
  const resolveMoneyUnit = (item = {}) => {
    const raw = toString(item.money_unit || item.moneyUnit || item.money_unit_hint || item.currency_unit || item.value_unit).toLowerCase();
    if (raw === 'major') return 'major';
    if (raw === 'minor') return 'minor';
    if (raw === 'auto') return 'auto';
    return 'minor';
  };
  const escapeForAttr = (value) => String(value || '').replace(/"/g, '&quot;');
  const slug = (value) => {
    return toString(value)
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const getCookie = (name) => {
    const safeName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const found = document.cookie.match(new RegExp(`(?:^|; )${safeName}=([^;]*)`));
    return found ? decodeURIComponent(found[1]) : null;
  };

  const hasConsent = () => state.consent || getCookie(CONSENT_COOKIE) === '1';

  const ensureConsent = () => {
    state.consent = hasConsent();
    return state.consent;
  };

  const safeItem = (item = {}) => {
    const id = toString(item.item_id || item.id);
    const name = toString(item.item_name || item.name);
    const moneyUnit = resolveMoneyUnit(item);
    return {
      item_id: id,
      item_name: name,
      item_brand: toString(item.item_brand || item.brand),
      item_category: toString(item.item_category || item.category),
      item_variant: toString(item.item_variant || item.variant_id),
      price: Number.isFinite(Number(item.price))
        ? toMoney(item.price, { unit: moneyUnit })
        : toMoney(item.price),
      quantity: Math.max(1, Math.max(0, parseInt(item.quantity, 10) || 1))
    };
  };

  const toMetaContentIds = (items = []) => items
    .map((item) => toString(item.item_id || item.id))
    .filter(Boolean);

  const getMetaValue = (items = []) => {
    const first = safeItem(items[0] || {});
    const total = items.reduce((acc, item) => {
      const normalized = safeItem(item);
      return acc + normalized.price * normalized.quantity;
    }, 0);
    return {
      value: Number(total || 0).toFixed(2),
      content_type: 'product',
      content_ids: toMetaContentIds(items),
      content_name: first.item_name || undefined,
      content_category: first.item_category || undefined,
      brand: first.item_brand || undefined,
    };
  };

  const emitToGtag = (eventName, payload) => {
    if (!GA_ID) return;
    if (!window.gtag) return;

    const eventBase = {
      currency: DEFAULT_CURRENCY,
      send_to: GA_ID,
    };

    if (eventName === 'view_item_list' || eventName === 'select_item' || eventName === 'view_item') {
      const items = (payload.items || []).map(safeItem);
      const total = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
      window.gtag('event', eventName, {
        ...eventBase,
        event_category: 'ecommerce',
        event_label: eventName,
        item_list_id: payload.item_list_id || undefined,
        item_list_name: payload.item_list_name || undefined,
        value: Number(total || 0).toFixed(2),
        ecommerce: {
          currency: DEFAULT_CURRENCY,
          value: Number(total || 0).toFixed(2),
          items,
        },
      });
      return;
    }

    if (eventName === 'add_to_cart') {
      const items = (payload.items || []).map(safeItem);
      const value = Number(items.reduce((sum, item) => sum + item.price * item.quantity, 0));
      window.gtag('event', eventName, {
        ...eventBase,
        event_category: 'ecommerce',
        value: value.toFixed(2),
        items,
        currency: DEFAULT_CURRENCY,
        ecommerce: { currency: DEFAULT_CURRENCY, value: value.toFixed(2), items },
      });
      return;
    }

    if (eventName === 'view_cart') {
      const items = (payload.items || []).map(safeItem);
      const value = Number(items.reduce((sum, item) => sum + item.price * item.quantity, 0));
      window.gtag('event', 'view_cart', {
        ...eventBase,
        event_category: 'ecommerce',
        value: value.toFixed(2),
        currency: DEFAULT_CURRENCY,
        ecommerce: { currency: DEFAULT_CURRENCY, value: value.toFixed(2), items },
      });
      return;
    }

    if (eventName === 'begin_checkout') {
      const items = (payload.items || []).map(safeItem);
      const value = Number(items.reduce((sum, item) => sum + item.price * item.quantity, 0));
      window.gtag('event', 'begin_checkout', {
        ...eventBase,
        event_category: 'funnel',
        value: value.toFixed(2),
        coupon: payload.coupon || undefined,
        currency: DEFAULT_CURRENCY,
        ecommerce: {
          currency: DEFAULT_CURRENCY,
          value: value.toFixed(2),
          items,
          coupon: payload.coupon || undefined,
        },
      });
      return;
    }

    if (eventName === 'purchase') {
      const items = (payload.items || []).map(safeItem);
      const value = Number(payload.value || 0);
      const transactionValue = Number.isFinite(value) && value > 0
        ? value
        : Number(items.reduce((sum, item) => sum + item.price * item.quantity, 0));
      window.gtag('event', 'purchase', {
        ...eventBase,
        event_category: 'ecommerce',
        transaction_id: toString(payload.transaction_id || payload.order_id || payload.order_name),
        affiliation: toString(payload.affiliation || ''),
        value: transactionValue.toFixed(2),
        currency: payload.currency || DEFAULT_CURRENCY,
        tax: toString(payload.tax),
        shipping: toString(payload.shipping),
        coupon: payload.coupon || undefined,
        items,
        ecommerce: {
          transaction_id: toString(payload.transaction_id || payload.order_id || payload.order_name),
          affiliation: toString(payload.affiliation || ''),
          value: transactionValue.toFixed(2),
          currency: payload.currency || DEFAULT_CURRENCY,
          tax: toString(payload.tax),
          shipping: toString(payload.shipping),
          coupon: payload.coupon || undefined,
          items,
        },
      });
      return;
    }

    if (eventName === 'search') {
      window.gtag('event', 'search', {
        ...eventBase,
        search_term: toString(payload.search_term),
      });
      return;
    }

    if (eventName === 'sign_up') {
      window.gtag('event', 'sign_up', {
        ...eventBase,
        method: toString(payload.method || 'newsletter'),
      });
      return;
    }
  };

  const emitToFacebook = (eventName, payload) => {
    if (!FB_ID || !window.fbq) return;

    const items = (payload.items || []).map(safeItem);
    const values = getMetaValue(items);
    const base = {
      content_type: 'product',
      content_ids: values.content_ids,
      currency: DEFAULT_CURRENCY,
      value: Number(values.value || 0)
    };

    if (eventName === 'view_item_list') {
      window.fbq('trackCustom', 'KuddyViewItemList', {
        list_name: toString(payload.item_list_name),
        item_count: items.length,
      });
      return;
    }

    if (eventName === 'select_item') {
      window.fbq('trackCustom', 'KuddySelectItem', {
        selected_item: items[0] ? items[0].item_name : undefined,
        content_type: 'product',
        content_ids: values.content_ids,
      });
      return;
    }

    if (eventName === 'view_item') {
      window.fbq('trackCustom', 'ViewContent', {
        ...base,
        content_name: items[0]?.item_name,
      });
      return;
    }

    if (eventName === 'add_to_cart') {
      window.fbq('track', 'AddToCart', {
        ...base,
        content_name: items[0]?.item_name || 'Kuddy Cart Add',
      });
      return;
    }

    if (eventName === 'view_cart') {
      window.fbq('trackCustom', 'KuddyViewCart', {
        ...base,
        item_count: items.length,
      });
      return;
    }

    if (eventName === 'begin_checkout') {
      window.fbq('trackCustom', 'KuddyBeginCheckout', {
        ...base,
        num_items: items.length,
      });
      return;
    }

    if (eventName === 'purchase') {
      window.fbq('track', 'Purchase', {
        ...base,
        value: Number((payload.value || values.value || 0)),
        currency: toString(payload.currency || DEFAULT_CURRENCY),
        num_items: items.length,
        content_name: items[0]?.item_name,
        transaction_id: toString(payload.transaction_id || payload.order_id || payload.order_name),
      });
      return;
    }

    if (eventName === 'search') {
      window.fbq('trackCustom', 'KuddySearch', {
        search_string: toString(payload.search_term),
        item_count: items.length || 0,
      });
      return;
    }

    if (eventName === 'sign_up') {
      window.fbq('track', 'CompleteRegistration', {
        content_name: 'Newsletter',
      });
      return;
    }
  };

  const normalizeMoneyToDecimal = (value, unit = 'minor', fallback = 0) => {
    if (Number.isFinite(Number(value))) {
      return toMoney(value, { unit });
    }
    if (typeof value === 'string') {
      const cleaned = value.replace(/,/g, '').replace(/[^\d.\-]/g, '');
      const parsed = Number(cleaned);
      if (Number.isFinite(parsed)) return toMoney(parsed, { unit });
    }
    return fallback;
  };

  const canEmit = () => {
    if (!hasConsent()) return false;
    if (GA_ID && !state.googleReady) return false;
    if (FB_ID && !state.metaReady) return false;
    return true;
  };

  const emit = (eventName, payload) => {
    const eventPayload = typeof payload === 'object' && payload ? payload : {};
    window.dataLayer.push({ event: eventName, ...eventPayload });
    emitToGtag(eventName, eventPayload);
    emitToFacebook(eventName, eventPayload);
  };

  const drainQueue = () => {
    if (!canEmit()) return;
    while (state.queue.length) {
      const item = state.queue.shift();
      if (!item) continue;
      const { event_name, payload } = item;
      emit(event_name, payload || {});
    }
  };

  const track = (eventName, payload = {}) => {
    const detail = { event_name: toString(eventName), timestamp: Date.now(), payload };
    if (!canEmit()) {
      state.queue.push(detail);
      return;
    }

    try {
      emit(detail.event_name, payload);
    } catch (_error) {
      state.queue.push(detail);
    }
  };

  const loadGoogle = () => {
    if (!GA_ID || window.__kuddyGoogleLoaded) return;
    window.__kuddyGoogleLoaded = true;

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_ID)}`;
    script.onload = () => {
      window.dataLayer = window.dataLayer || [];
      window.gtag = window.gtag || function() { window.dataLayer.push(arguments); };
      state.googleReady = true;
      window.gtag('js', new Date());
      window.gtag('consent', 'default', {
        ad_storage: 'denied',
        analytics_storage: 'denied',
      });
      if (hasConsent()) {
        window.gtag('consent', 'update', {
          ad_storage: 'granted',
          analytics_storage: 'granted',
        });
      }
      window.gtag('config', GA_ID, { send_page_view: false, anonymize_ip: true });
      drainQueue();
    };
    script.onerror = () => {
      state.googleReady = true;
      drainQueue();
    };
    document.head.appendChild(script);
  };

  const loadMeta = () => {
    if (!FB_ID || window.__kuddyMetaLoaded) return;
    window.__kuddyMetaLoaded = true;

    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    script.onload = () => {
      if (!window.fbq) {
        window.fbq = function () {
          window.fbq.callMethod ? window.fbq.callMethod.apply(window.fbq, arguments) : window.fbq.queue.push(arguments);
        };
        window.fbq.queue = [];
      }
      window.fbq.l = Date.now();
      state.metaReady = true;
      window.fbq('init', FB_ID);
      window.fbq('track', 'PageView');
      drainQueue();
    };
    script.onerror = () => {
      state.metaReady = true;
      drainQueue();
    };
    document.head.appendChild(script);
  };

  // Load vendor scripts off the critical path: events queue in the meantime and
  // drain once the scripts arrive, so nothing is lost by waiting for idle time
  // or the visitor's first interaction (capped at 4s).
  const whenIdle = (callback) => {
    let done = false;
    const run = () => {
      if (done) return;
      done = true;
      ['pointerdown', 'keydown', 'touchstart', 'scroll'].forEach((type) => {
        window.removeEventListener(type, run);
      });
      callback();
    };
    ['pointerdown', 'keydown', 'touchstart', 'scroll'].forEach((type) => {
      window.addEventListener(type, run, { once: true, passive: true });
    });
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(run, { timeout: 4000 });
    } else {
      setTimeout(run, 3000);
    }
  };

  const enableTracking = () => {
    if (state.inited) return;
    state.inited = true;
    state.consent = true;

    if (!GA_ID && !FB_ID) {
      drainQueue();
      return;
    }
    whenIdle(() => {
      if (GA_ID) loadGoogle();
      if (FB_ID) loadMeta();
    });
  };

  const readCartItems = async () => {
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) return [];
      const cart = await response.json();
      return Array.isArray(cart?.items)
        ? cart.items.map((item) => ({
            id: String(item.product_id || item.id || ''),
            item_id: String(item.product_id || item.id || ''),
            item_name: String(item.product_title || item.title || item.name || ''),
            name: String(item.product_title || item.title || item.name || ''),
            item_brand: String(item.vendor || item.product?.vendor || ''),
            item_category: String(item.product_type || item.product?.type || ''),
            item_variant: String(item.variant_id || item.variantId || item.variant_id?.toString() || ''),
            price: toMoney(item.final_price || item.price),
            quantity: Number(item.quantity || 1),
          }))
        : [];
    } catch (_error) {
      return [];
    }
  };

  const sendBeginCheckout = async () => {
    const items = await readCartItems();
    const value = items.reduce((sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 1), 0);
    track('begin_checkout', {
      items,
      value,
      currency: DEFAULT_CURRENCY,
    });
  };

  const setupCheckoutClick = () => {
    const body = document.body;
    body?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-analytics-checkout]');
      if (!button) return;
      sendBeginCheckout();
    });
  };

  const setupSearchSubmit = () => {
    const body = document.body;
    body?.addEventListener('submit', async (event) => {
      const form = event.target.closest('[data-analytics-search-form]');
      if (!form) return;
      const formData = new FormData(form);
      track('search', {
        search_term: String(formData.get('q') || '').trim(),
        search_type: String(formData.get('type') || ''),
      });
    });
  };

  const sendViewCart = async () => {
    if (document.body.dataset.kuddyAnalyticsCartViewed === '1') return;
    const path = location.pathname.replace(/\/+$/, '').toLowerCase();
    if (!path.endsWith('/cart') && path !== '/cart') return;

    document.body.dataset.kuddyAnalyticsCartViewed = '1';
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      const items = Array.isArray(cart.items) ? cart.items.map((item) => ({
        id: String(item.product_id || item.id || ''),
        item_id: String(item.product_id || item.id || ''),
        name: item.product_title || item.title || '',
        item_name: item.product_title || item.title || '',
        brand: item.vendor || '',
        item_brand: item.vendor || '',
        category: item.product_type || item.product?.type || '',
        item_category: item.product_type || item.product?.type || '',
        price: toMoney(item.final_price || item.price),
        quantity: item.quantity,
      })) : [];
      track('view_cart', { items, value: items.reduce((sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 1), 0) });
    } catch (_error) {
      // ignore network failures
    }
  };

  const sendViewItem = (root = document) => {
    const section = root.querySelector('[data-kuddy-product-analytics]');
    if (!section || document.body.dataset.kuddyAnalyticsProductViewed === '1') return;
    try {
      const product = JSON.parse(section.textContent || '{}');
      if (!product || !product.id) return;
      document.body.dataset.kuddyAnalyticsProductViewed = '1';
        track('view_item', {
          currency: DEFAULT_CURRENCY,
          items: [
            {
            id: String(product.id),
            item_id: String(product.id),
            item_name: String(product.item_name || product.name || ''),
            name: String(product.item_name || product.name || ''),
            item_brand: String(product.brand || ''),
            item_category: String(product.category || ''),
            price: toMoney(product.price),
            quantity: 1,
            }
          ]
        });
    } catch (_error) {
      // no-op
    }
  };

  const setupSignUpFromInline = () => {
    const marker = document.querySelector('[data-kuddy-newsletter-success]');
    const form = document.querySelector('[data-kuddy-newsletter-form]');
    if (form) {
      form.addEventListener('submit', () => {
        track('sign_up', {
          method: 'newsletter'
        });
      }, { once: true });
    }
    if (!marker || marker.dataset.kuddyNewsletterTracked === '1') return;
    if (!marker.textContent?.trim()) return;
    marker.dataset.kuddyNewsletterTracked = '1';
    track('sign_up', {
      method: 'newsletter'
    });
  };

  const trackSelectItem = (item) => {
    if (!item || !item.item_id) return;
    track('select_item', {
      item_list_id: slug(item.item_list_name || ''),
      item_list_name: item.item_list_name || '',
      items: [item]
    });
  };

  const bootstrap = () => {
    state.consent = hasConsent();

    setupCheckoutClick();
    setupSearchSubmit();
    setupSignUpFromInline();

    if (document.body.dataset.kuddyAnalyticsInitialized === '1') return;
    document.body.dataset.kuddyAnalyticsInitialized = '1';

    const path = location.pathname.replace(/\/+$/, '').toLowerCase();

    const isCart = path.endsWith('/cart') || path === '/cart';
    const isCollection = path.includes('/collections/');
    const isSearch = path.includes('/search');
    const isProduct = path.includes('/products/');
    const isOrder = path.includes('/orders/');

    if (isProduct) {
      sendViewItem(document);
    }

    if (isCart) {
      sendViewCart();
    }

    if (isSearch && !document.querySelector('[data-kuddy-search-results-ready]')) {
      const searchText = document.querySelector('.search__input')?.value || '';
      if (searchText.trim()) {
        track('search', {
          search_term: searchText.trim(),
          search_type: new URLSearchParams(location.search).get('type') || undefined,
        });
      }
    }

    if (isCollection || isSearch) {
      const listSection = document.querySelector('[data-analytics-list]');
      if (listSection && listSection.dataset.kuddyAnalyticsListTracked !== '1') {
        const listName = listSection.dataset.analyticsListName || document.title || 'Collection';
        const items = Array.from(listSection.querySelectorAll('[data-analytics-product-card]')).map((card) => ({
          id: toString(card.dataset.analyticsProductId),
          item_id: toString(card.dataset.analyticsProductId),
          item_name: toString(card.dataset.analyticsProductName),
          item_brand: toString(card.dataset.analyticsProductBrand),
          item_category: toString(card.dataset.analyticsProductCategory),
          price: toMoney(card.dataset.analyticsProductPrice),
          quantity: 1,
        })).filter((item) => item.item_id);

        listSection.dataset.kuddyAnalyticsListTracked = '1';

        track('view_item_list', {
          item_list_id: slug(listName),
          item_list_name: listName,
          items,
          currency: DEFAULT_CURRENCY,
        });

        document.body.addEventListener('click', (event) => {
          const card = event.target.closest('[data-analytics-product-card]');
          if (!card) return;
          const link = event.target.closest('a[data-analytics-product-link]');
          if (!link || link.closest('[data-analytics-product-card]') !== card) return;
          const item = {
            id: toString(card.dataset.analyticsProductId),
            item_id: toString(card.dataset.analyticsProductId),
            item_name: toString(card.dataset.analyticsProductName),
            item_brand: toString(card.dataset.analyticsProductBrand),
            item_category: toString(card.dataset.analyticsProductCategory),
            price: toMoney(card.dataset.analyticsProductPrice),
            quantity: 1,
            item_list_name: listName,
            item_list_id: slug(listName),
          };
          if (item.item_id) trackSelectItem(item);
        }, { capture: true });
      }
    }

    if (isOrder) {
      const orderPayloadEl = document.querySelector('[data-kuddy-order-analytics]');
      if (orderPayloadEl && !document.body.dataset.kuddyAnalyticsPurchaseTracked) {
        document.body.dataset.kuddyAnalyticsPurchaseTracked = '1';
        try {
          const orderPayload = JSON.parse(orderPayloadEl.textContent || '{}');
          const items = Array.isArray(orderPayload.items) ? orderPayload.items.map((item) => safeItem({
            ...item,
            money_unit: item.money_unit || 'major',
          })) : [];
          if (orderPayload?.transaction_id) {
            track('purchase', {
              transaction_id: toString(orderPayload.transaction_id),
              affiliation: toString(orderPayload.affiliation),
              value: normalizeMoneyToDecimal(orderPayload.value || 0, 'major'),
              tax: normalizeMoneyToDecimal(orderPayload.tax || 0, 'major'),
              shipping: normalizeMoneyToDecimal(orderPayload.shipping || 0, 'major'),
              currency: toString(orderPayload.currency || DEFAULT_CURRENCY),
              items,
              coupon: toString(orderPayload.coupon || ''),
            });
          }
        } catch (_error) {
          // ignore invalid payload
        }
      }
    }

    if (state.consent) {
      if (GA_ID) loadGoogle();
      if (FB_ID) loadMeta();
    }
  };

  window.kuddyAnalytics = {
    setConsent: enableTracking,
    track,
    trackAddToCart: (items = [], detail = {}) => {
      const normalized = items.map(safeItem).filter((item) => item.item_id);
      track('add_to_cart', {
        ...detail,
        items: normalized,
        currency: DEFAULT_CURRENCY,
        value: normalized.reduce((sum, item) => sum + item.price * item.quantity, 0)
      });
    },
    trackSearch: (searchTerm, searchType) => track('search', { search_term: searchTerm, search_type: searchType }),
    trackBeginCheckout: () => sendBeginCheckout(),
    trackViewItemList: (payload = {}) => track('view_item_list', payload),
    trackSelectItem: (payload = {}) => track('select_item', payload),
    trackViewItem: (payload = {}) => track('view_item', payload),
    trackSignUp: () => track('sign_up', { method: 'newsletter' }),
    refresh: bootstrap,
    hasConsent,
    setConsentCookieName: (name) => {
      if (typeof name === 'string' && name) {
        // noop placeholder for compatibility
      }
    }
  };

  const syncIfConsent = () => {
    if (ensureConsent()) {
      enableTracking();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }

  document.addEventListener('kuddy:analytics:consent', syncIfConsent, false);
  window.addEventListener('storage', (event) => {
    if (!event.key || event.key !== CONSENT_COOKIE) return;
    syncIfConsent();
  });

  if (document.body) {
    setTimeout(syncIfConsent, 350);
  }
})();
